// app/frontend/components/ui/theme.ts
import { createTheme, MantineColorsTuple } from "@mantine/core";

// Primary Cosmic Orange palette
const cosmicOrange: MantineColorsTuple = [
  "#fff4e6", // 0 - very light orange
  "#ffe6cc", // 1 - light orange
  "#ffcc99", // 2 - soft orange
  "#ffb366", // 3 - medium orange
  "#ff9933", // 4 - bright orange
  "#ff8000", // 5 - primary orange
  "#e66f00", // 6 - deeper orange
  "#cc5f00", // 7 - dark orange
  "#b34e00", // 8 - darker orange
  "#993d00", // 9 - deepest orange
];

// Secondary Orange palette (for accent/variety)
const accentOrange: MantineColorsTuple = [
  "#fff7ed", // 0 - very light amber
  "#ffedd5", // 1 - light amber
  "#fed7aa", // 2 - soft amber
  "#fdba74", // 3 - medium amber
  "#fb923c", // 4 - bright amber
  "#f97316", // 5 - primary amber
  "#ea580c", // 6 - deeper amber
  "#dc2626", // 7 - red-orange
  "#b91c1c", // 8 - darker red-orange
  "#991b1b", // 9 - deepest red-orange
];

// Spacey Grays
const spaceGray: MantineColorsTuple = [
  "#f8fafc", // 0 - almost white
  "#f1f5f9", // 1 - very light gray
  "#e2e8f0", // 2 - light gray
  "#cbd5e1", // 3 - medium light gray
  "#94a3b8", // 4 - medium gray
  "#64748b", // 5 - gray
  "#475569", // 6 - dark gray
  "#334155", // 7 - darker gray
  "#1e293b", // 8 - very dark gray
  "#0f172a", // 9 - almost black
];

export const cosmicTheme = createTheme({
  colors: {
    orange: cosmicOrange,
    amber: accentOrange,
    gray: spaceGray,
    // Primary cosmic color is orange
    cosmic: cosmicOrange,
    accent: accentOrange, // Secondary orange for variety
  },

  primaryColor: "cosmic", // Orange
  primaryShade: { light: 5, dark: 4 },

  defaultRadius: "lg",

  // Body: Public Sans — genuine reading face (USWDS federal .gov long-form),
  // holds up at 17-19px for the 2,600-word blog posts.
  // Headings: Bricolage Grotesque — variable font with an optical-size axis,
  // pairs with Public Sans on a same-family-axis-different-personality contrast.
  // Both loaded from Google Fonts in application.html.erb. Rubik was replaced
  // per the impeccable audit (P0 #3): Rubik is a display grotesque designed
  // for signage, not a reading face — see vidhive.io-audit/impeccable/TYPOGRAPHY.md.
  fontFamily:
    '"Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  headings: {
    fontFamily:
      '"Bricolage Grotesque", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: "600",
  },

  shadows: {
    xs: "0 1px 2px rgba(0, 0, 0, 0.05)",
    sm: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
    md: "0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)",
    lg: "0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)",
    xl: "0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)",
  },

  components: {
    Button: {
      defaultProps: {
        radius: "xl",
        size: "md",
        fw: 600,
      },
      styles: {
        root: {
          transition: "all 0.2s ease",
          "&:hover": {
            transform: "translateY(-1px)",
          },
          "&[data-disabled]": {
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            color: "rgba(255, 255, 255, 0.3)",
            borderColor: "rgba(255, 255, 255, 0.1)",
            cursor: "not-allowed",
            opacity: 1, // Override default opacity
            transform: "none",
          },
        },
      },
    },

    Card: {
      defaultProps: {
        radius: "xl",
        shadow: "sm",
        withBorder: false,
        p: "lg",
      },
      styles: {
        root: {
          // Solid surface — no backdrop-filter. Glassmorphism as a system
          // default was flagged in the impeccable audit as an absolute-ban
          // violation + a scroll-perf tax (Safari especially). Individual
          // components that genuinely need frosted-glass (modal overlays,
          // sticky navs) can opt-in explicitly.
          background: "rgba(255, 255, 255, 0.02)",
          border: "1px solid rgba(255, 128, 0, 0.1)",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 20px 40px rgba(255, 128, 0, 0.1)",
          },
        },
      },
    },

    TextInput: {
      defaultProps: {
        radius: "md",
        size: "md",
      },
      styles: {
        input: {
          color: "#f1f5f9",
          "&::placeholder": {
            color: "#94a3b8",
          },
        },
      },
    },

    Textarea: {
      defaultProps: {
        radius: "md",
        size: "md",
      },
      styles: {
        input: {
          color: "#f1f5f9",
          "&::placeholder": {
            color: "#94a3b8",
          },
        },
      },
    },

    Notification: {
      defaultProps: {
        radius: "lg",
      },
    },

    Badge: {
      defaultProps: {
        radius: "xl",
        variant: "light",
      },
    },

    Modal: {
      defaultProps: {
        radius: "xl",
        overlayProps: { blur: 4 },
      },
    },

    Drawer: {
      defaultProps: {
        radius: "xl",
        overlayProps: { blur: 4 },
      },
    },

    Text: {
      styles: {
        root: {
          color: "#f1f5f9", // Light text for dark cosmic backgrounds
        },
      },
    },

    Title: {
      styles: {
        root: {
          color: "#f8fafc", // Even lighter for headings
        },
      },
    },

    Tooltip: {
      defaultProps: {
        withArrow: true,
        position: "top",
      },
      styles: {
        tooltip: {
          backgroundColor: "rgba(30, 27, 24, 0.95)",
          color: "#f1f5f9",
          border: "1px solid rgba(255, 128, 0, 0.2)",
          backdropFilter: "blur(8px)",
          fontSize: "0.8125rem",
          padding: "8px 12px",
        },
        arrow: {
          backgroundColor: "rgba(30, 27, 24, 0.95)",
          border: "1px solid rgba(255, 128, 0, 0.2)",
        },
      },
    },
  },
});
